import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Alert } from '@mui/material';
import { AccessTime as TimeIcon } from '@mui/icons-material';

/**
 * SessionTimer Component
 * Displays remaining time for 15-minute consultation session
 */
const SessionTimer = ({ startTime, duration = 900 }) => {
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (!startTime) return;

    const calculateTimeRemaining = () => {
      const start = new Date(startTime);
      const now = new Date();
      const elapsed = Math.floor((now - start) / 1000); // seconds
      const remaining = Math.max(0, duration - elapsed);

      setTimeRemaining(remaining);
      setShowWarning(remaining <= 60 && remaining > 0);

      if (remaining === 0) {
        // Session ended
        return false;
      }
      return true;
    };

    // Initial calculation
    if (!calculateTimeRemaining()) {
      return;
    }

    // Update every second
    const interval = setInterval(() => {
      if (!calculateTimeRemaining()) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, duration]);

  const formatTimeRemaining = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!startTime) return null;

  return (
    <Box>
      <Chip
        icon={<TimeIcon />}
        label={formatTimeRemaining(timeRemaining)}
        color={showWarning ? 'error' : 'default'}
        variant={showWarning ? 'filled' : 'outlined'}
        size="small"
      />

      {showWarning && (
        <Alert
          severity="warning"
          sx={{
            position: 'absolute',
            top: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            minWidth: '300px'
          }}
        >
          ⚠️ Session ending in {timeRemaining} second{timeRemaining !== 1 ? 's' : ''}
        </Alert>
      )}
    </Box>
  );
};

export default SessionTimer;
